import { z } from 'zod'

/**
 * The Library's columns: the fixed vocabulary, the build's own (`field:<key>`), and the
 * workspace's layout of them (CLI-1009) — which columns show, in what order, how wide, how tall
 * the rows, the density. The layout is the workspace's, kept with its stream: everyone who opens
 * the workspace sees the same Library, and Analytics filters by the build columns it shows.
 */

export const COLUMN_IDS = [
  'headline',
  'mediaType',
  'link',
  'outlet',
  'author',
  'folder',
  'topic',
  'tag',
  'date',
  'sentiment',
  'classification',
  'reach',
  'snippet',
] as const
export type BuiltinColumnId = (typeof COLUMN_IDS)[number]
/** A column beyond the fixed row — a named field or the build's own column (stream-columns.ts). */
export type FieldColumnId = `field:${string}`
export const FIELD_COLUMN_PREFIX = 'field:'
export const columnIdSchema = z.union([
  z.enum(COLUMN_IDS),
  z.custom<FieldColumnId>(
    (value) =>
      typeof value === 'string' &&
      value.startsWith(FIELD_COLUMN_PREFIX) &&
      value.length > FIELD_COLUMN_PREFIX.length &&
      value.length <= 120,
    'A field column is "field:<name>"',
  ),
])
export type ColumnId = z.infer<typeof columnIdSchema>

/**
 * A layout's columns: the built-in ones plus any of the build's, which a client's build can carry
 * dozens of — a bound on the payload, not on what a reader may choose.
 */
export const LIBRARY_VIEW_COLUMNS_MAX = 150

export const LIBRARY_DENSITIES = ['compact', 'comfortable', 'tall'] as const
export type LibraryDensity = (typeof LIBRARY_DENSITIES)[number]

/** Row heights are kept per row, the most recent this many. */
const LIBRARY_ROWS_MAX = 400

/** The workspace's Library: its columns in order, the widths and row heights someone set, the
 *  density. Null on the stream = the default layout. */
export const libraryLayoutSchema = z.object({
  columns: z.array(columnIdSchema).min(1).max(LIBRARY_VIEW_COLUMNS_MAX),
  widths: z.record(z.string().max(130), z.number().int().min(20).max(2000)).default({}),
  rows: z
    .record(z.string().max(80), z.number().int().min(10).max(2000))
    .refine((rows) => Object.keys(rows).length <= LIBRARY_ROWS_MAX, 'Too many row heights')
    .default({}),
  density: z.enum(LIBRARY_DENSITIES).default('comfortable'),
})
export type LibraryLayout = z.infer<typeof libraryLayoutSchema>

/** A column id's build key, or null for one of the fixed columns. */
export const fieldKeyOf = (id: ColumnId): string | null =>
  id.startsWith(FIELD_COLUMN_PREFIX) ? id.slice(FIELD_COLUMN_PREFIX.length) : null

/** The build columns a column list shows, by key, in its order — the workspace's chosen build
 *  columns, which both pages filter by. */
export const chosenOf = (columns: readonly ColumnId[]): string[] =>
  columns.flatMap((id) => fieldKeyOf(id) ?? [])
